/**
 * AI PROVIDER ABSTRACTION — agents reference a provider by key, never by
 * import. Swapping "Creator Manager" from Anthropic to OpenAI, or adding
 * Gemini, is an edit here plus a matching entry in services/ai/, never a
 * change inside a module that USES an agent.
 */
export type ProviderKey = 'anthropic' | 'openai' | 'gemini';

export interface AIProviderConfig {
  key: ProviderKey;
  displayName: string;
  /** models this provider currently exposes to agents — extend freely */
  models: string[];
  /** true once real API credentials exist in the backend's secret store */
  configured: boolean;
}

export const AI_PROVIDERS: Record<ProviderKey, AIProviderConfig> = {
  anthropic: { key: 'anthropic', displayName: 'Anthropic', models: ['claude-sonnet', 'claude-opus'], configured: false },
  openai:    { key: 'openai',    displayName: 'OpenAI',    models: ['gpt-4.1', 'gpt-4.1-mini'],       configured: false },
  gemini:    { key: 'gemini',    displayName: 'Gemini',    models: ['gemini-2.5-pro'],                 configured: false },
};

export interface AgentConfig {
  id: string;
  nameKey: string;
  provider: ProviderKey;
  model: string;
  permissionLevel: 1 | 2 | 3;
}

/**
 * Changing "Creator Manager" from Claude to GPT-4.1 is this one line:
 *   provider: 'openai', model: 'gpt-4.1'
 * No module that reads AGENTS needs to change — they only ever read
 * AI_PROVIDERS[agent.provider].displayName, never a hardcoded brand name.
 */
export const AGENTS: AgentConfig[] = [
  { id: 'executive',        nameKey: 'agent.executive',       provider: 'anthropic', model: 'claude-sonnet', permissionLevel: 1 },
  { id: 'creator_manager',  nameKey: 'agent.creatorManager',  provider: 'anthropic', model: 'claude-sonnet', permissionLevel: 2 },
  { id: 'content_director', nameKey: 'agent.contentDirector', provider: 'openai',    model: 'gpt-4.1',       permissionLevel: 2 },
  { id: 'social_analytics', nameKey: 'agent.socialAnalytics', provider: 'anthropic', model: 'claude-sonnet', permissionLevel: 1 },
  { id: 'sales',            nameKey: 'agent.sales',           provider: 'openai',    model: 'gpt-4.1',       permissionLevel: 2 },
  { id: 'developer',        nameKey: 'agent.developer',       provider: 'anthropic', model: 'claude-sonnet', permissionLevel: 2 },
];
